"use client";

import * as React from "react";
import {
  Home,
  Building2,
  FileText,
  Menu,
  X,
  LogOut,
  MapPin,
  Smartphone,
  UserCog,
  PhoneCall,
  BarChart2,
  CalendarCheck,
  BookOpen,
  Activity,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { clearStoredAuth } from "../utils/authStorage";

export function SuperAdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname.split("/")[2] || "dashboard";
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);

  const menuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <Home className="w-5 h-5" />,
      path: "/dashboard/dashboard",
    },
    {
      id: "schools",
      label: "Manage Customers",
      icon: <Building2 className="w-5 h-5" />,
      path: "/dashboard/schools",
    },
    {
      id: "billing",
      label: "Billing",
      icon: <CreditCard className="w-5 h-5" />,
      path: "/dashboard/billing",
    },
    {
      id: "super-school-admins",
      label: "Super Customer Admins",
      icon: <UserCog className="w-5 h-5" />,
      path: "/dashboard/super-school-admins",
    },
    {
      id: "app-version",
      label: "App Version",
      icon: <Smartphone className="w-5 h-5" />,
      path: "/dashboard/app-version",
    },
    {
      id: "public-leads",
      label: "Public Leads",
      icon: <PhoneCall className="w-5 h-5" />,
      path: "/dashboard/public-leads",
    },
    {
      id: "page-analytics",
      label: "Page Analytics",
      icon: <BarChart2 className="w-5 h-5" />,
      path: "/dashboard/page-analytics",
    },
    {
      id: "api-analytics",
      label: "API Analytics",
      icon: <Activity className="w-5 h-5" />,
      path: "/dashboard/api-analytics",
    },
    {
      id: "demo-bookings",
      label: "Demo Bookings",
      icon: <CalendarCheck className="w-5 h-5" />,
      path: "/dashboard/demo-bookings",
    },
    {
      id: "blog-management",
      label: "Blog",
      icon: <BookOpen className="w-5 h-5" />,
      path: "/dashboard/blog-management",
    },
    {
      id: "system-logs",
      label: "System Logs",
      icon: <FileText className="w-5 h-5" />,
      path: "/dashboard/system-logs",
    },
    {
      id: "bus-tracking",
      label: "Bus Tracking",
      icon: <MapPin className="w-5 h-5" />,
      path: "/dashboard/bus-tracking",
    },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="flex h-full bg-gray-50 relative overflow-hidden">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        className="lg:hidden fixed top-4 left-4 z-50 p-2"
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Menu className="w-6 h-6" />
        )}
      </Button>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Responsive Sidebar */}
      <aside
        className={`
          fixed lg:static
          top-0 bottom-0 left-0
          w-[280px] lg:w-72
          bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
          z-40
          flex flex-col
        `}
        style={{ height: '100vh' }}
      >


        {/* Navigation Menu - Scrollable */}
        <nav
          className="flex-1 overflow-y-auto overflow-x-hidden p-4"
          style={{ 
            maxHeight: 'calc(100vh - 180px)',
            minHeight: 0
          }}
        >
          <div className="space-y-1">
            {menuItems.map((item) => (
              <Button
                key={item.id}
                variant={currentTab === item.id ? "secondary" : "ghost"}
                className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                  currentTab === item.id
                    ? "bg-[#0263cc] text-white"
                    : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                }`}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) {
                    setIsSidebarOpen(false);
                  }
                }}
              >
                {item.icon}
                <span className="ml-3 whitespace-nowrap">{item.label}</span>
              </Button>
            ))}
          </div>

        {/* Bottom Section - Fixed at bottom */}
        <div 
          className="flex-shrink-0 p-4 border-t border-gray-200 bg-white"
          style={{ marginTop: 'auto' }}
        >
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px]"
            onClick={() => {
              clearStoredAuth();
              window.location.href = "/login";
            }}
          >
            <LogOut className="w-5 h-5" />
            <span className="ml-3">Logout</span>
          </Button>
        </div>
        </nav>

      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto lg:ml-0 w-full">
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          <Outlet key={location.pathname} />
        </div>
      </main>
    </div>
  );
}

